import { useEffect, useState, useCallback } from 'react';
import { Client } from '@microsoft/microsoft-graph-client';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import 'isomorphic-fetch';

const graphScopes = ['user.read', 'profile'];

export function useGraphApi() {
  const { instance, accounts } = useMsal();
  const [graphClient, setGraphClient] = useState(null);

  const getAccessToken = useCallback(async () => {
    try {
      const result = await instance.acquireTokenSilent({
        scopes: graphScopes,
        account: accounts[0],
      });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const result = await instance.acquireTokenPopup({
            scopes: graphScopes,
          });
          return result.accessToken;
        } catch (err) {
          console.error('Error acquiring token:', err);
          throw err;
        }
      }
      throw error;
    }
  }, [instance, accounts]);

  useEffect(() => {
    const initializeGraphClient = async () => {
      if (accounts.length > 0) {
        try {
          const accessToken = await getAccessToken();
          const client = Client.init({
            authProvider: (done) => {
              done(null, accessToken);
            },
          });
          setGraphClient(client);
        } catch (error) {
          console.error('Error initializing Graph client:', error);
        }
      }
    };

    initializeGraphClient();
  }, [accounts, getAccessToken]);

  const getUserPhoto = useCallback(async () => {
    if (!graphClient) return null;

    try {
      const photo = await graphClient
        .api('/me/photo/$value')
        .get();
      return URL.createObjectURL(photo);
    } catch (error) {
      if (error.statusCode === 404) {
        console.log('No profile photo available');
        return null;
      }
      console.error('Error fetching user photo:', error);
      return null;
    }
  }, [graphClient]);

  const getUserProfile = useCallback(async () => {
    if (!graphClient) return null;

    try {
      const profile = await graphClient
        .api('/me')
        .select('displayName,mail,userPrincipalName,jobTitle')
        .get();
      return profile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }, [graphClient]);

  return {
    getUserPhoto,
    getUserProfile,
  };
} 